import { initLabelShapeMorph, initIconShapeMorph } from '../utils/shape-morph'

/**
 * Brand button shape morph
 * v2.0.0
 *
 * Morphs .btn-cta, .btn-pill, .btn-icon, and .custom-select__trigger from a
 * rounded pill/circle at rest into the faceted brand shape on hover/focus,
 * using a native `clip-path: path()` transition instead of hand-matched
 * clip-path polygons or a JS-driven tween. The hover/focus/active states
 * that trigger the morph (:hover, .cursor-follow, .--active) are plain CSS
 * selectors in components/button.css and components/custom-select.css -
 * this only measures each shape and keeps its two clip-path targets current.
 */

// Morphs are kept per element so a rebuild can re-measure an existing shape
// instead of re-running init and duplicating a ResizeObserver.
const morphRegistry = new WeakMap()

const remember = (el, morphs) => {
    morphRegistry.set(el, [...(morphRegistry.get(el) ?? []), ...morphs])
    return morphs
}

const initCta = (btnEl) => {
    if (morphRegistry.has(btnEl)) return

    const textShapeEl = btnEl.querySelector('.btn-cta__text .btn-cta__shape')
    const iconShapeEl = btnEl.querySelector('.btn-cta__icon .btn-cta__shape')

    if (!textShapeEl || !iconShapeEl) return

    remember(btnEl, [initLabelShapeMorph(textShapeEl), initIconShapeMorph(iconShapeEl)])
}

const initPill = (btnEl) => {
    if (morphRegistry.has(btnEl)) return

    const shapeEl = btnEl.querySelector('.btn-pill__shape')
    if (!shapeEl) return

    remember(btnEl, [initLabelShapeMorph(shapeEl)])
}

const initIcon = (btnEl) => {
    if (morphRegistry.has(btnEl)) return

    const shapeEl = btnEl.querySelector('.btn-icon__shape')
    if (!shapeEl) return

    remember(btnEl, [initIconShapeMorph(shapeEl)])
}

const initSelectTrigger = (triggerEl) => {
    if (morphRegistry.has(triggerEl)) return

    const shapeEl = triggerEl.querySelector('.custom-select__trigger-shape')
    if (!shapeEl) return

    remember(triggerEl, [initLabelShapeMorph(shapeEl)])
}

const initFormSearch = (formEl) => {
    if (morphRegistry.has(formEl)) return

    const shapeEl = formEl.querySelector('.form-search__shape')
    if (!shapeEl) return

    remember(formEl, [initLabelShapeMorph(shapeEl)])
}

// `root` lets callers re-run this against just newly-injected markup (e.g.
// after an AJAX content swap) instead of re-initializing every button on the
// page and duplicating their ResizeObservers.
const btnMorph = (root = document) => {
    root.querySelectorAll('.btn-cta').forEach(initCta)
    root.querySelectorAll('.btn-pill').forEach(initPill)
    root.querySelectorAll('.btn-icon').forEach(initIcon)
    root.querySelectorAll('.custom-select__trigger').forEach(initSelectTrigger)
    root.querySelectorAll('.form-search').forEach(initFormSearch)
}

const SHAPE_SELECTOR = '.btn-cta, .btn-pill, .btn-icon, .custom-select__trigger, .form-search'

// Re-measure shapes inside `root`. Needed whenever something that was
// display:none becomes visible: those shapes measured 0-width at init and
// bailed before ever getting their clip-path custom properties set.
export const rebuildShapes = (root = document) => {
    const elements = [...root.querySelectorAll(SHAPE_SELECTOR)]

    if (root instanceof Element && root.matches(SHAPE_SELECTOR)) {
        elements.push(root)
    }

    elements.forEach((el) => morphRegistry.get(el)?.forEach((morph) => morph.rebuild()))
}

document.addEventListener('DOMContentLoaded', () => btnMorph())

// Fired by anything that reveals previously hidden markup (see the mobile
// menu), or that inserts a brand new subtree (see x-clone) whose shapes were
// never measured in the first place. initCta/initPill/initIcon/
// initSelectTrigger already guard against re-initializing an element twice,
// so running both here is safe regardless of which case this is.
document.addEventListener('shape:rebuild', (event) => {
    const root = event.target instanceof Element ? event.target : document
    btnMorph(root)
    rebuildShapes(root)
})

export default btnMorph
